import { Trans } from '@lingui/macro'
import { CurrencyAmount, Percent, Token } from '@uniswap/sdk-core'
import votingRewardABi from 'abis/votingReward.json'
import { BribesButton, ClaimButton, Pending } from 'components/Button'
import Column from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { IconLoadingBubble, LongLoadingBubble, MediumLoadingBubble, SmallLoadingBubble } from 'components/Loading'
import Row, { RowBetween } from 'components/Row'
import { NoLiquityTips } from 'components/Tips'
import { ToastError } from 'components/Toast'
import Tooltip from 'components/Tooltip'
import { useActiveChainId, useEthersSigner } from 'connection/useActiveChainId'
import { useToken } from 'hooks/Tokens'
import { TooltipBox, TooltipRow } from 'pages/Liquidity/LiquidityRow'
import { StyledTableRowM, TextLeftCell, TextRightCell } from 'pages/Liquidity/LiquidityRow-m'
import { ChangeEvent, CSSProperties, ForwardedRef, forwardRef, ReactNode, useCallback, useMemo, useState } from 'react'
import { Box } from 'rebass'
import { useVoteCurrentId } from 'state/http/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { ThemedText } from 'theme'
import { getCurrRound, getWeeklyId } from 'utils/CurrRound'
import { formatAmount } from 'utils/formatAmout'
import { formatCurrencyAmount, NumberType } from 'utils/formatCurrency'
import { handlerError } from 'utils/formatError'
import { getContract } from 'utils/getContract'
import { unwrappedToken } from 'utils/unwrappedToken'

import { ReactComponent as MoreIcon } from '../../assets/imgs/more.svg'
import AddBribesModal from './AddBribesModal'
import { VoteSortMethod } from './state'
import { rewardPorps } from './types'
import { LoadedRowProps, MaxBtn, TotalVoteEarnsItem, VoteBox, VoteInput } from './VoteRow'

/* Token Row: skeleton row component */
function VoteRow({
  header,
  pool,
  tvl,
  vApr,
  fees,
  bribes,
  totalRewards,
  totalVotes,
  myVote,
  earn,
  ...rest
}: {
  header?: boolean
  first?: boolean
  $loading?: boolean
  fees: ReactNode
  tvl?: ReactNode
  vApr: ReactNode
  pool: ReactNode
  bribes: ReactNode
  totalRewards: ReactNode
  totalVotes: ReactNode
  myVote: ReactNode
  earn: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <TextLeftCell data-testid="pool-cell">{pool}</TextLeftCell>
      <Row align="flex-start" gap="sm">
        <TextLeftCell data-testid="tvl-cell">{tvl}</TextLeftCell>
        <TextRightCell data-testid="vApr-cell">{vApr}</TextRightCell>
        <TextRightCell data-testid="fees-cell">{fees}</TextRightCell>
      </Row>
      <RowBetween align="flex-start" gap="sm">
        <TextLeftCell data-testid="total-rewards-cell">{totalRewards}</TextLeftCell>
        <TextRightCell data-testid="total-votes-cell">{totalVotes}</TextRightCell>
        <TextRightCell data-testid="bribes-cell">{bribes}</TextRightCell>
      </RowBetween>
      <RowBetween align="flex-start" gap="sm">
        <TextLeftCell data-testid="my-vote-cell" className="bg">
          {myVote}
        </TextLeftCell>
        <TextRightCell data-testid="earn-cell">{earn}</TextRightCell>
      </RowBetween>
    </>
  )
  return <StyledTableRowM {...rest}>{rowCells}</StyledTableRowM>
}

/* Loading State: row component with loading bubbles */
export function LoadingRow(props: { first?: boolean; last?: boolean }) {
  return (
    <VoteRow
      $loading
      pool={
        <RowBetween>
          <Row gap="sm">
            <IconLoadingBubble width="40px" height="40px" />
            <SmallLoadingBubble />
          </Row>
          <MoreIcon />
        </RowBetween>
      }
      tvl={
        <Column align="flex-start" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.TVL}</Trans>
          </ThemedText.TextSecondary>
          <LongLoadingBubble />
        </Column>
      }
      vApr={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.VAPR}</Trans>
          </ThemedText.TextSecondary>
          <LongLoadingBubble />
        </Column>
      }
      fees={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.FEES}</Trans>
          </ThemedText.TextSecondary>
          <LongLoadingBubble />
        </Column>
      }
      totalRewards={
        <Column align="flex-start" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.TOTAL_REWARDS}</Trans>
          </ThemedText.TextSecondary>
          <MediumLoadingBubble />
        </Column>
      }
      totalVotes={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.TOTAL_VOTES}</Trans>
          </ThemedText.TextSecondary>
          <MediumLoadingBubble />
          <MediumLoadingBubble />
        </Column>
      }
      bribes={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.BRIBES}</Trans>
          </ThemedText.TextSecondary>
          <Column align="flex-end" gap="sm">
            <SmallLoadingBubble />
            <BribesButton>+ Bribes</BribesButton>
          </Column>
        </Column>
      }
      myVote={
        <Column align="flex-start" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.MY_VOTE}</Trans>
          </ThemedText.TextSecondary>
          <SmallLoadingBubble />
          <SmallLoadingBubble />
        </Column>
      }
      earn={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{VoteSortMethod.EARN}</Trans>
          </ThemedText.TextSecondary>
          <SmallLoadingBubble />
          <SmallLoadingBubble />
          <ClaimButton>Claim</ClaimButton>
        </Column>
      }
      {...props}
    />
  )
}

const TotalRewardsItem = ({ rewardAddr }: { rewardAddr: rewardPorps }) => {
  const { chainId } = useActiveChainId()
  const currenty = useMemo(
    () =>
      chainId
        ? new Token(
            chainId,
            rewardAddr.tokenInfo.id,
            Number(rewardAddr.tokenInfo.decimals),
            rewardAddr.tokenInfo.symbol,
            rewardAddr.tokenInfo.name
          )
        : undefined,
    [
      chainId,
      rewardAddr.tokenInfo.decimals,
      rewardAddr.tokenInfo.id,
      rewardAddr.tokenInfo.name,
      rewardAddr.tokenInfo.symbol,
    ]
  )

  const tokenInfo = useMemo(() => {
    if (!currenty) return
    return CurrencyAmount.fromRawAmount(currenty, rewardAddr?.rewardAmount || 0)
  }, [currenty, rewardAddr.rewardAmount])
  return (
    <Box alignItems="center" display="flex" justifyContent="center">
      <ThemedText.TextSecondary fontSize={14}>
        {formatAmount(tokenInfo?.toSignificant(), 2, true)} {currenty?.symbol}
      </ThemedText.TextSecondary>
      {rewardAddr.tokenInfo?.shit && <NoLiquityTips />}
    </Box>
  )
}

/* Loaded State: row component with token information */
export const LoadedRow = forwardRef((props: LoadedRowProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { voteListIndex, voteListLength, vote, inputRef, onChange, inputValue } = props
  const [timeCount, setTimeCount] = useState(10)
  const [showAddBribes, setShowAddBribes] = useState(false)
  const [showHover_TVL, setShowHover_TVL] = useState(false)
  const [showHover_fees, setShowHover_fees] = useState(false)
  const [showHover_rewards, setShowHover_rewards] = useState(false)

  const addTransaction = useTransactionAdder()
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false)
  const [txHash, setTxHash] = useState<string>('')
  const [txError, setTxError] = useState<string>('')
  const { account } = useActiveChainId()
  const currRound = getCurrRound()
  const singerProvider = useEthersSigner()

  const handleDismissAddBribes = () => {
    setShowAddBribes(false)
  }

  const handleAmountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value, vote)
    },
    [onChange, vote]
  )

  const onClaim = useCallback(
    async (data: any) => {
      setAttemptingTxn(true)

      const contract = getContract(data.votingRewardAddress, votingRewardABi.abi, singerProvider)

      try {
        const response = await contract.getReward(account, currRound)
        setAttemptingTxn(false)
        addTransaction(response, {
          type: TransactionType.CLAIM_REWARDS,
          token0Address: '',
          token1Address: '',
        })
        setTxHash(response.hash)
      } catch (error) {
        setAttemptingTxn(false)
        setTxError(handlerError(error))
        ToastError(handlerError(error))
        console.log('create Lock error', error)
      }
    },
    [account, addTransaction, currRound, singerProvider]
  )

  const epochNum = useVoteCurrentId()
  const currentEpochId = getWeeklyId()
  const isCurrEpoch = useMemo(() => {
    return currentEpochId === epochNum
  }, [currentEpochId, epochNum])

  const token0 = useToken(vote.token0.id)
  const token1 = useToken(vote.token1.id)
  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  return (
    <>
      <div ref={ref} data-testid={`vote-table-row-${vote.lpAddr}`}>
        <VoteRow
          pool={
            <RowBetween>
              <Row gap="sm">
                <DoubleCurrencyLogo currency0={currency1} currency1={currency0} size={40} margin />
                <Column gap="xs" align="flex-start" ml="8px">
                  <ThemedText.TextPrimary fontWeight={700} fontSize={16}>
                    {vote.token0.symbol}-{vote.token1.symbol}
                  </ThemedText.TextPrimary>
                  <ThemedText.TextPrimary fontWeight={700} fontSize={16}>
                    {new Percent(vote.poolFee, 1_000_000).toSignificant()}%
                  </ThemedText.TextPrimary>
                </Column>
              </Row>
              <MoreIcon />
            </RowBetween>
          }
          tvl={
            <Column align="flex-start" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.TVL}</Trans>
              </ThemedText.TextSecondary>
              <Tooltip
                show={showHover_TVL}
                text={
                  <TooltipBox>
                    <Row justify="space-between" alignItems="flex-start" className="tooltipItem">
                      <ThemedText.TextSecondary fontSize={14}>
                        {formatCurrencyAmount(vote.liqvidityToken0, NumberType.FiatTokenStats2)} {vote.token0.symbol}
                      </ThemedText.TextSecondary>
                    </Row>
                    <Row justify="space-between" alignItems="flex-start" className="tooltipItem">
                      <ThemedText.TextSecondary fontSize={14}>
                        {formatCurrencyAmount(vote.liqvidityToken1, NumberType.FiatTokenStats2)} {vote.token1.symbol}
                      </ThemedText.TextSecondary>
                    </Row>
                  </TooltipBox>
                }
              >
                <TooltipRow onMouseEnter={() => setShowHover_TVL(true)} onMouseLeave={() => setShowHover_TVL(false)}>
                  <ThemedText.TextPrimary fontWeight={700}>${formatAmount(vote.tvl, 2, true)}</ThemedText.TextPrimary>
                </TooltipRow>
              </Tooltip>
            </Column>
          }
          vApr={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.VAPR}</Trans>
              </ThemedText.TextSecondary>
              <ThemedText.TextPrimary fontWeight={700}>
                {formatAmount(Number(vote.vAPR) * 100, 2, true)}%
              </ThemedText.TextPrimary>
            </Column>
          }
          fees={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.FEES}</Trans>
              </ThemedText.TextSecondary>
              <Tooltip
                show={showHover_fees}
                text={
                  <TooltipBox>
                    {vote.feeReward?.map((item: any, index: number) => {
                      return (
                        <Row justify="space-between" alignItems="flex-start" className="tooltipItem" key={index}>
                          <TotalRewardsItem key={'fees' + index} rewardAddr={item} />
                        </Row>
                      )
                    })}
                  </TooltipBox>
                }
              >
                <TooltipRow onMouseEnter={() => setShowHover_fees(true)} onMouseLeave={() => setShowHover_fees(false)}>
                  ${formatAmount(vote.votetotalFeeRewardsUSD, 2, true)}
                </TooltipRow>
              </Tooltip>
            </Column>
          }
          totalRewards={
            <Column align="flex-start" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.TOTAL_REWARDS}</Trans>
              </ThemedText.TextSecondary>
              <Tooltip
                show={showHover_rewards}
                text={
                  <TooltipBox>
                    {vote.rewardsList && vote.rewardsList.length > 0 ? (
                      vote.rewardsList.map((item, index: number) => {
                        return <TotalRewardsItem key={'vote-rewardItem' + index} rewardAddr={item} />
                      })
                    ) : (
                      <ThemedText.TextSecondary fontSize={14}>--</ThemedText.TextSecondary>
                    )}
                  </TooltipBox>
                }
              >
                <TooltipRow
                  onMouseEnter={() => setShowHover_rewards(true)}
                  onMouseLeave={() => setShowHover_rewards(false)}
                >
                  <ThemedText.TextPrimary fontWeight={700}>
                    {' '}
                    ${formatAmount(vote.votetotalRewardsUSD, 2, true)}
                  </ThemedText.TextPrimary>
                </TooltipRow>
              </Tooltip>
            </Column>
          }
          totalVotes={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.TOTAL_VOTES}</Trans>
              </ThemedText.TextSecondary>
              <ThemedText.TextPrimary fontWeight={700}>
                {' '}
                {formatAmount(Number(vote?.weeklyVotedTotal) / Math.pow(10, 18), 2, true)}
              </ThemedText.TextPrimary>
              <ThemedText.TextSecondary fontSize={14}>{vote.poolWeightRatio?.toFixed(2, 1)}%</ThemedText.TextSecondary>
            </Column>
          }
          bribes={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.BRIBES}</Trans>
              </ThemedText.TextSecondary>
              <Column align="flex-end" gap="sm">
                {vote.bribeReward?.map((item: any, index: number) => {
                  return <TotalRewardsItem key={'bribeReward' + index} rewardAddr={item} />
                })}
                {isCurrEpoch && (
                  <BribesButton
                    onClick={() => {
                      setShowAddBribes(true)
                      setTimeCount(10)
                    }}
                  >
                    + Bribes
                  </BribesButton>
                )}
              </Column>
            </Column>
          }
          myVote={
            <Column width="100%" align="flex-start" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.MY_VOTE}</Trans>
              </ThemedText.TextSecondary>
              <ThemedText.TextPrimary fontWeight={700}>
                {' '}
                {formatAmount(Number(vote.epochVote) / Math.pow(10, 18), 2, true)}
              </ThemedText.TextPrimary>
              <ThemedText.TextSecondary fontSize={14}>
                {Number(vote.epochVoteTotal) == 0 || Number(vote.epochVote) == 0
                  ? 0
                  : ((Number(vote.epochVote) / Number(vote.epochVoteTotal)) * 100).toFixed(2)}
                %
              </ThemedText.TextSecondary>
              {isCurrEpoch && (
                <VoteBox>
                  <VoteInput
                    type="number"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    placeholder=""
                    key={vote.lpAddr}
                    id={'VoteInput' + vote.lpAddr}
                    onChange={handleAmountChange}
                    value={inputValue[vote.lpAddr]?.value}
                    ref={inputRef}
                  />
                  <ThemedText.TextPrimary ml="4px" mr="4px">
                    %
                  </ThemedText.TextPrimary>
                  <MaxBtn>
                    <div
                      className="active-gradient-text"
                      onClick={() => {
                        onChange(100, vote)
                      }}
                    >
                      Max
                    </div>
                  </MaxBtn>
                </VoteBox>
              )}
            </Column>
          }
          earn={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{VoteSortMethod.EARN}</Trans>
              </ThemedText.TextSecondary>
              {vote.rewardsList && vote.rewardsList?.length > 0 ? (
                vote.rewardsList?.map((item, index: number) => {
                  return (
                    <TotalVoteEarnsItem
                      key={'earn-vote' + index}
                      rewardAddr={item}
                      poolAddr={vote.votingRewardAddress}
                    />
                  )
                })
              ) : (
                <ThemedText.TextRewards>--</ThemedText.TextRewards>
              )}
              <Row justify="end">
                <ClaimButton disabled={attemptingTxn} onClick={() => onClaim(vote)}>
                  {attemptingTxn ? <Pending /> : 'Claim'}
                </ClaimButton>
              </Row>
            </Column>
          }
          first={voteListIndex === 0}
          last={voteListIndex === voteListLength - 1}
        />
      </div>
      <AddBribesModal
        brigeType="VOTE"
        isOpen={showAddBribes}
        details={vote}
        count={timeCount}
        setCount={setTimeCount}
        onDismiss={handleDismissAddBribes}
      />
    </>
  )
})

LoadedRow.displayName = 'LoadedRow'
